import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QRCodeModule } from 'angularx-qrcode';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { HandoffService } from '../../services/handoff.service';
import { ErrorReportingService } from '../../services/error-reporting.service';

/**
 * "Continue on your phone" handoff screen (DEV_SPEC_desktop_capture_warning.md
 * §4.1). Shown when a desktop/tablet user taps the face-scan capture step: a
 * QR (client-side, token only) that carries her session to her phone with no
 * re-login. The QR is the only handoff method. A signed-in token lives 5 min on
 * the server and is single-use, so it is re-minted quietly before it expires;
 * the old code stays on screen until the new one is ready (no flicker).
 */
@Component({
  selector: 'app-phone-handoff',
  standalone: true,
  imports: [CommonModule, QRCodeModule, TranslatePipe],
  template: `
    <div class="ph-wrap">
      <button
        type="button"
        class="ph-back"
        (click)="back.emit()"
        [attr.aria-label]="'resultDetail.close' | translate"
      >
        &times;
      </button>

      <p class="ph-step">{{ 'phoneHandoff.step' | translate }}</p>
      <h1 class="ph-title">{{ 'phoneHandoff.title' | translate }}</h1>
      <p class="ph-body">{{ 'phoneHandoff.body' | translate }}</p>

      <div class="ph-qr-card" *ngIf="!error">
        <qrcode
          *ngIf="deepLink"
          [qrdata]="deepLink"
          [width]="224"
          [margin]="0"
          [errorCorrectionLevel]="'M'"
          [colorDark]="'#2a2730'"
          [colorLight]="'#ffffff'"
          [alt]="'phoneHandoff.title' | translate"
        ></qrcode>
        <div class="ph-spinner" *ngIf="!deepLink" aria-label="loading"></div>
      </div>

      <div class="ph-error" *ngIf="error">
        <p>{{ 'phoneHandoff.qrError' | translate }}</p>
        <button type="button" class="ph-retry" (click)="retry()" [disabled]="loading">
          {{ 'phoneHandoff.retry' | translate }}
        </button>
      </div>

      <p class="ph-hint" *ngIf="!error">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18h2" />
        </svg>
        {{ 'phoneHandoff.hint' | translate }}
      </p>
    </div>
  `,
  styles: [
    `
      :host {
        --ph-ink: #2a2730;
        --ph-ink-soft: #5f5866;
        --ph-line: #ece7ef;
        --ph-eyebrow: #8a6282;
        --ph-brand: var(--brand-color, #d1409c);
        display: block;
      }
      .ph-wrap {
        position: relative;
        max-width: 440px;
        margin: 0 auto;
        padding: 40px 24px;
        text-align: center;
      }
      .ph-back {
        position: absolute;
        top: 12px;
        inset-inline-end: 16px;
        width: 36px;
        height: 36px;
        border: none;
        background: rgba(0, 0, 0, 0.05);
        border-radius: 50%;
        font-size: 22px;
        line-height: 1;
        color: var(--ph-ink-soft);
        cursor: pointer;
      }
      .ph-step {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--ph-eyebrow);
        margin: 0 0 12px;
      }
      .ph-title {
        font-size: 28px;
        font-weight: 600;
        color: var(--ph-ink);
        margin: 0 0 10px;
        line-height: 1.25;
      }
      .ph-body {
        font-size: 16px;
        line-height: 1.55;
        color: var(--ph-ink-soft);
        margin: 0 auto 24px;
        max-width: 340px;
      }
      .ph-qr-card {
        width: 264px;
        height: 264px;
        margin: 0 auto;
        padding: 20px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        border: 1px solid var(--ph-line);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(42, 39, 48, 0.04), 0 8px 28px rgba(42, 39, 48, 0.06);
      }
      .ph-qr-card ::ng-deep img,
      .ph-qr-card ::ng-deep canvas {
        display: block;
        width: 224px;
        height: 224px;
      }
      .ph-spinner {
        width: 34px;
        height: 34px;
        border: 3px solid var(--ph-line);
        border-top-color: var(--ph-brand);
        border-radius: 50%;
        animation: ph-spin 0.8s linear infinite;
      }
      @keyframes ph-spin {
        to {
          transform: rotate(360deg);
        }
      }
      .ph-hint {
        display: flex;
        gap: 8px;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        color: var(--ph-ink-soft);
        margin: 16px 0 0;
      }
      .ph-hint svg {
        flex: none;
        color: var(--ph-eyebrow);
      }
      .ph-error {
        color: var(--ph-ink-soft);
        font-size: 15px;
        margin: 8px 0 0;
      }
      .ph-retry {
        margin-top: 12px;
        min-height: 44px;
        padding: 0 22px;
        border: none;
        border-radius: 10px;
        background: var(--ph-brand);
        color: #fff;
        font-family: inherit;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;
      }
      .ph-retry:disabled {
        opacity: 0.55;
        cursor: default;
      }
      @media (max-width: 600px) {
        .ph-wrap {
          padding-top: 32px;
        }
        .ph-title {
          font-size: 24px;
        }
      }
    `,
  ],
})
export class PhoneHandoffComponent implements OnInit, OnDestroy {
  /** Scan context (usually null at step 1 — the capture is the first step). */
  @Input() scanId: string | null = null;
  @Input() step = 'selfie';

  /** Emitted when the user closes the handoff (× back to the steps list). */
  @Output() back = new EventEmitter<void>();

  loading = false;
  error = false;
  deepLink = '';

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    private handoff: HandoffService,
    private reporter: ErrorReportingService,
  ) {}

  ngOnInit(): void {
    void this.mint();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearTimer();
  }

  retry(): void {
    if (this.loading) return;
    this.error = false;
    void this.mint();
  }

  /**
   * Mint a code. On a refresh the current QR stays visible until the new one
   * arrives; a failed refresh retries once quietly (the old code still works
   * until its own expiry) before showing the error with a retry button.
   */
  private async mint(attempt = 0): Promise<void> {
    if (this.loading || this.destroyed) return;
    this.loading = true;
    let ok = false;
    try {
      const created = await this.handoff.create(this.scanId, this.step);
      if (this.destroyed) return;
      if (created?.url) {
        this.deepLink = created.url;
        this.error = false;
        ok = true;
        this.schedule(created.ttlSec);
      }
    } catch (e) {
      this.reporter.report(e, 'phone-handoff');
    } finally {
      this.loading = false;
    }
    if (ok || this.destroyed) return;
    if (this.deepLink && attempt === 0) {
      this.clearTimer();
      this.refreshTimer = setTimeout(() => void this.mint(1), 10_000);
      return;
    }
    this.deepLink = '';
    this.error = true;
  }

  /** Re-mint 30s before the server expiry (guest links never expire: ttl 0). */
  private schedule(ttlSec: number): void {
    this.clearTimer();
    if (!ttlSec || ttlSec <= 0) return;
    const ms = Math.max(15, ttlSec - 30) * 1000;
    this.refreshTimer = setTimeout(() => void this.mint(), ms);
  }

  private clearTimer(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }
}
